import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Users, HandCoins, MessageCircle, MapPin, CalendarClock, ShieldCheck } from "lucide-react";

export default function ExchangeBuddy() {
    const { user } = useAuth();

    return (
        <div data-testid="exchange-buddy-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Exchange Buddy</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 max-w-3xl">
                Save on <span className="text-primary">agency fees</span>. Help each other move abroad.
            </h1>
            <p className="text-lg text-muted-foreground mt-5 max-w-2xl leading-relaxed">
                An <strong>Exchange Buddy</strong> is someone moving in the opposite direction of you.
                They already live where you're going — you already live where they're going.
                Skip <strong>agency fees</strong>, share housing tips, and land softly on the other side.
            </p>

            <div className="grid md:grid-cols-3 gap-5 mt-14">
                <FeatureCard
                    icon={<HandCoins className="w-5 h-5" />}
                    title="Save on agency fees"
                    body="Real students share leads and tips instead of you paying a broker a month's rent for the same info."
                />
                <FeatureCard
                    icon={<CalendarClock className="w-5 h-5" />}
                    title="Flexible dates"
                    body="No need to match exact dates. Buddies match within a ±6-month window — same year, same semester, roughly."
                />
                <FeatureCard
                    icon={<MessageCircle className="w-5 h-5" />}
                    title="Chat right away"
                    body="Once matched, message your buddy directly. Ask about the university, the district, the paperwork, the parties."
                />
                <FeatureCard
                    icon={<Users className="w-5 h-5" />}
                    title="No room needed"
                    body="You don't need to swap or list a room. Just tell us where you're going and where you live — that's enough to match."
                />
                <FeatureCard
                    icon={<MapPin className="w-5 h-5" />}
                    title="Local advice"
                    body="Which neighbourhoods to avoid, cheap groceries, which contract to sign — the stuff no landing page tells you."
                />
                <FeatureCard
                    icon={<ShieldCheck className="w-5 h-5" />}
                    title="100% free"
                    body="ErasmusSwap is free to use. Exchange Buddy is a bonus on top of the room-swap features."
                />
            </div>

            <div className="mt-14 rounded-3xl bg-primary text-primary-foreground p-8 sm:p-12">
                <h2 className="font-display text-3xl sm:text-4xl font-bold max-w-xl">Ready to find your buddy?</h2>
                <p className="text-primary-foreground/85 mt-3 max-w-xl">
                    {user
                        ? "You're already logged in. Head over to Matches to see your Exchange Buddy suggestions."
                        : "Create a free account, tell us where you're going, and we'll match you with someone who knows that city inside out."}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                    {user ? (
                        <Link to="/matches">
                            <Button data-testid="exchange-buddy-cta-matches" className="rounded-full bg-background text-foreground hover:bg-background/90 h-11 px-6">
                                See my buddies
                            </Button>
                        </Link>
                    ) : (
                        <>
                            <Link to="/register">
                                <Button data-testid="exchange-buddy-cta-signup" className="rounded-full bg-background text-foreground hover:bg-background/90 h-11 px-6">
                                    Sign up free
                                </Button>
                            </Link>
                            <Link to="/browse">
                                <Button variant="outline" data-testid="exchange-buddy-cta-browse" className="rounded-full h-11 px-6 border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10">
                                    Browse rooms first
                                </Button>
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function FeatureCard({ icon, title, body }) {
    return (
        <div className="bg-white border border-border/60 rounded-2xl p-6">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                {icon}
            </div>
            <div className="font-display text-lg font-semibold mt-4">{title}</div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{body}</p>
        </div>
    );
}
